import { useEffect, useState } from "react";
import { api, apiError } from "../../lib/api";
import { Section, LevelBadge, PageHeader } from "../../components/fields";
import { THEME_PRESETS } from "../../lib/goalMeta";
import { toast } from "sonner";

const ContactEditor = ({ school, onSaved }) => {
  const c = school.contacts?.[0] || {};
  const [form, setForm] = useState({ name: c.name || "", email: c.email || "", title: c.title || "" });
  const save = async () => {
    try {
      await api.put(`/schools/${school.id}/branding`, { contacts: form.name.trim() ? [form] : [] });
      toast.success("School contact updated");
      onSaved();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="mt-2.5 pt-2.5 border-t border-slate-100">
      <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500 mb-1.5">School contact</p>
      <div className="flex items-center gap-2 flex-wrap">
        <input data-testid={`contact-name-${school.id}`} placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
          className="rounded-md border border-slate-200 px-2 py-1 text-xs w-32 focus:outline-none focus:ring-2 focus:ring-emerald-600" />
        <input data-testid={`contact-email-${school.id}`} placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
          className="rounded-md border border-slate-200 px-2 py-1 text-xs w-44 focus:outline-none focus:ring-2 focus:ring-emerald-600" />
        <input data-testid={`contact-title-${school.id}`} placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
          className="rounded-md border border-slate-200 px-2 py-1 text-xs w-28 focus:outline-none focus:ring-2 focus:ring-emerald-600" />
        <button data-testid={`save-contact-btn-${school.id}`} onClick={save}
          className="text-xs font-semibold bg-emerald-50 text-emerald-700 hover:bg-emerald-100 px-3 py-1.5 rounded-full transition-colors duration-150">Save</button>
      </div>
    </div>
  );
};

const BrandingEditor = ({ school, onSaved }) => {
  const [form, setForm] = useState({ name: school.name, themeKey: school.theme?.key || "", goal_check: !!school.settings?.goal_check_enabled, logo: undefined });
  const [saving, setSaving] = useState(false);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 500 * 1024) { toast.error("Logo must be under 500KB"); return; }
    const reader = new FileReader();
    reader.onload = () => setForm((x) => ({ ...x, logo: reader.result }));
    reader.readAsDataURL(f);
  };

  const save = async () => {
    setSaving(true);
    const preset = THEME_PRESETS.find((t) => t.key === form.themeKey);
    const theme = preset && preset.key ? { key: preset.key, primary: preset.primary, dark: preset.dark, soft: preset.soft, soft_dark: preset.soft_dark, soft_border: preset.soft_border, accent: preset.accent } : null;
    try {
      await api.put(`/schools/${school.id}/branding`, {
        name: form.name, theme, goal_check_enabled: form.goal_check,
        ...(form.logo !== undefined && { logo: form.logo }),
      });
      toast.success("Branding saved");
      onSaved();
    } catch (e) { toast.error(apiError(e)); }
    setSaving(false);
  };

  return (
    <div className="mt-3 pt-3 border-t border-slate-100 space-y-2.5">
      <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500">Branding & features</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        <input data-testid={`branding-name-${school.id}`} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="School display name" className="rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600" />
        <select data-testid={`branding-theme-${school.id}`} value={form.themeKey} onChange={(e) => setForm({ ...form, themeKey: e.target.value })}
          className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm">
          {THEME_PRESETS.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
        </select>
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        {(form.logo ?? school.logo) && <img src={form.logo ?? school.logo} alt="logo" className="h-9 rounded-lg border border-slate-200 bg-white p-0.5 object-contain" />}
        <label className="text-xs font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 px-3 py-2 rounded-lg cursor-pointer transition-colors duration-150">
          Upload logo
          <input data-testid={`branding-logo-input-${school.id}`} type="file" accept="image/*" onChange={onFile} className="hidden" />
        </label>
        {(form.logo ?? school.logo) && (
          <button data-testid={`branding-logo-remove-${school.id}`} onClick={() => setForm({ ...form, logo: "" })} className="text-xs font-semibold text-red-600 hover:underline">Remove logo</button>
        )}
        <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer ml-auto">
          <input data-testid={`goal-check-toggle-${school.id}`} type="checkbox" checked={form.goal_check}
            onChange={(e) => setForm({ ...form, goal_check: e.target.checked })} className="accent-emerald-700 w-4 h-4" />
          Goal Check enabled
        </label>
      </div>
      <button data-testid={`save-branding-btn-${school.id}`} onClick={save} disabled={saving || !form.name.trim()}
        className="text-xs font-semibold bg-emerald-700 hover:bg-emerald-800 text-white px-4 py-2 rounded-full transition-colors duration-150 disabled:opacity-50">
        {saving ? "Saving…" : "Save branding"}
      </button>
    </div>
  );
};

const TabBtn = ({ active, onClick, children, testId }) => (
  <button data-testid={testId} onClick={onClick}
    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-150 ${active ? "bg-emerald-600 text-white shadow-sm" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-100"}`}>
    {children}
  </button>
);

const Input = ({ label, testId, ...props }) => (
  <div>
    <label className="text-xs font-bold tracking-[0.06em] uppercase text-slate-500">{label}</label>
    <input data-testid={testId} {...props} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600" />
  </div>
);

export default function AdminPanel() {
  const [tab, setTab] = useState("compliance");
  const [users, setUsers] = useState([]);
  const [students, setStudents] = useState([]);
  const [schools, setSchools] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [compliance, setCompliance] = useState(null);
  const [audit, setAudit] = useState([]);
  const [overdueGoals, setOverdueGoals] = useState([]);
  const [uForm, setUForm] = useState({ name: "", email: "", password: "", role: "aide", school_id: "" });
  const [sForm, setSForm] = useState({ first_name: "", last_name: "", grade: "", school_id: "" });
  const [aForm, setAForm] = useState({ aide_id: "", student_id: "", school_id: "", start_date: new Date().toISOString().slice(0, 10) });
  const [scForm, setScForm] = useState({ name: "", contract_start: "", contract_end: "" });
  const [remTimes, setRemTimes] = useState({});

  const load = () => {
    api.get("/users").then((r) => setUsers(r.data));
    api.get("/students").then((r) => setStudents(r.data));
    api.get("/schools").then((r) => setSchools(r.data));
    api.get("/assignments").then((r) => setAssignments(r.data));
    api.get("/admin/compliance").then((r) => setCompliance(r.data));
    api.get("/admin/audit-logs", { params: { limit: 100 } }).then((r) => setAudit(r.data));
    api.get("/goals/overdue").then((r) => setOverdueGoals(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const post = async (path, body, reset) => {
    try { await api.post(path, body); toast.success("Created"); reset(); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const setStatus = async (id, status) => {
    try { await api.put(`/users/${id}/status?status=${status}`); toast.success(status === "active" ? "Reactivated" : "Deactivated — access revoked"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const saveReminderTime = async (school) => {
    const t = remTimes[school.id] ?? school.settings?.eod_reminder_time ?? "15:30";
    try { await api.put(`/schools/${school.id}/settings`, { eod_reminder_time: t }); toast.success(`Reminder time set to ${t}`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const schoolName = (id) => schools.find((s) => s.id === id)?.name || "—";
  const [pv, setPv] = useState({ school_id: "", role: "school_admin", aide_id: "" });
  const startPreview = async () => {
    try {
      const r = await api.post("/admin/preview/start", pv);
      localStorage.setItem("sf_admin_token", localStorage.getItem("sf_token"));
      localStorage.setItem("sf_token", r.data.token);
      window.location.href = r.data.role === "aide" ? "/aide" : r.data.role === "sped_teacher" ? "/sped" : "/overview";
    } catch (e) { toast.error(apiError(e)); }
  };
  const pvAides = users.filter((u) => u.status === "active" && (pv.role === "sped_teacher" ? u.role === "sped_teacher" : u.role === "aide" && u.school_id === pv.school_id));
  const tabGroups = [
    ["Operations", [["compliance", "Compliance"], ["audit", "Audit Log"]]],
    ["Management", [["users", "Users"], ["students", "Students"], ["assignments", "Assignments"], ["schools", "Schools"]]],
  ];

  return (
    <div className="space-y-6 stagger">
      <PageHeader title="TAG Admin" subtitle="Client portals, users, students, assignments, compliance, and audit trail" />

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[["Schools", schools.length], ["Users", users.length], ["Students", students.length], ["Active assignments", assignments.filter((a) => a.status === "active").length], ["Open flags", compliance?.open_flags ?? "—"]].map(([l, v]) => (
          <div key={l} data-testid={`admin-stat-${l.toLowerCase().replace(/\s+/g, "-")}`} className="bg-white border border-slate-200/60 rounded-xl px-4 py-3">
            <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">{l}</p>
            <p className="font-heading text-2xl font-bold text-slate-900">{v}</p>
          </div>
        ))}
      </div>

      <div data-testid="preview-as-card" className="bg-white border border-slate-200/60 rounded-xl px-4 py-3.5 flex items-end gap-3 flex-wrap">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-1">Preview as</p>
          <select data-testid="preview-school-select" value={pv.school_id} onChange={(e) => setPv({ ...pv, school_id: e.target.value, aide_id: "" })}
            className="rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600">
            <option value="">Select school…</option>
            {schools.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <select data-testid="preview-role-select" value={pv.role} onChange={(e) => setPv({ ...pv, role: e.target.value, aide_id: "" })}
          className="rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600">
          <option value="school_admin">School Admin</option>
          <option value="aide">Aide</option>
          <option value="sped_teacher">Special Education Teacher</option>
        </select>
        {(pv.role === "aide" || pv.role === "sped_teacher") && (
          <select data-testid="preview-aide-select" value={pv.aide_id} onChange={(e) => setPv({ ...pv, aide_id: e.target.value })}
            className="rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600">
            <option value="">{pv.role === "aide" ? "Select aide…" : "Select teacher…"}</option>
            {pvAides.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        )}
        <button data-testid="start-preview-btn" onClick={startPreview} disabled={!pv.school_id || (pv.role !== "school_admin" && !pv.aide_id)}
          className="text-sm font-semibold bg-[#132238] hover:bg-[#1E3354] text-white px-4 py-2 rounded-[10px] transition-colors duration-150 disabled:opacity-40">
          Start preview
        </button>
        <p className="text-[11px] text-slate-400 basis-full sm:basis-auto">Read-only — no changes, emails, or notifications are possible while previewing.</p>
      </div>

      <div className="flex gap-x-6 gap-y-3 flex-wrap">
        {tabGroups.map(([group, items]) => (
          <div key={group}>
            <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-slate-400 mb-1.5">{group}</p>
            <div className="flex gap-2 flex-wrap">
              {items.map(([k, label]) => <TabBtn key={k} testId={`admin-tab-${k}`} active={tab === k} onClick={() => setTab(k)}>{label}</TabBtn>)}
            </div>
          </div>
        ))}
      </div>

      {tab === "compliance" && compliance && (
        <div className="space-y-4">
          {overdueGoals.length > 0 && (
            <Section title="Goal Check — data past due">
              <div className="space-y-2">
                {overdueGoals.map((g) => (
                  <div key={g.goal_id} data-testid={`overdue-goal-${g.goal_id}`} className="flex items-center gap-3 border border-amber-200 bg-amber-50/60 rounded-lg p-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800">{g.student_name} · <span className="font-semibold">{g.area}</span> <span className="text-xs text-slate-400">({g.goal_type})</span></p>
                      <p className="text-xs text-slate-500">Aide: {g.aides.join(", ")} · schedule: {g.schedule} · last entry: {g.last_check || "never"}</p>
                    </div>
                    <span className="text-[10px] font-bold text-amber-800 bg-amber-100 border border-amber-300 px-2 py-0.5 rounded-full">PAST DUE</span>
                  </div>
                ))}
              </div>
            </Section>
          )}
          {compliance.alerts.filter((a) => a.type !== "schedule_mismatch").length > 0 && (
            <div data-testid="compliance-alerts" className="bg-red-50 border border-red-200 rounded-xl p-4 space-y-1">
              {compliance.alerts.filter((a) => a.type !== "schedule_mismatch").map((a, i) => <p key={i} className="text-sm text-red-800">⚠ {a.message}</p>)}
            </div>
          )}
          {compliance.alerts.filter((a) => a.type === "schedule_mismatch").length > 0 && (
            <div data-testid="mismatch-alerts" className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-1">
              {compliance.alerts.filter((a) => a.type === "schedule_mismatch").map((a, i) => <p key={i} className="text-sm text-amber-800">⚠ {a.message}</p>)}
            </div>
          )}
          <Section title="Documentation completion by aide (last 10 school days)">
            <div className="space-y-3">
              {compliance.aide_stats.map((a) => (
                <div key={a.id}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="font-medium text-slate-700">{a.name} <span className="text-xs text-slate-400">· {a.school}</span>
                      {a.rate != null && a.window_days > 0 && a.window_days < 10 && (
                        <span data-testid={`window-days-${a.id}`} className="ml-2 text-[10px] font-bold text-sky-700 bg-sky-50 border border-sky-200 px-2 py-0.5 rounded-full">Day {a.window_days} of 10</span>
                      )}
                    </span>
                    {a.schedule_mismatch ? (
                      <span data-testid={`schedule-mismatch-${a.id}`} className="text-[11px] font-bold text-amber-800 bg-amber-100 border border-amber-300 px-2 py-0.5 rounded-full">Schedule mismatch ({a.actual}/{a.expected})</span>
                    ) : a.rate == null ? (
                      <span data-testid={`not-started-${a.id}`} className="text-xs font-semibold text-slate-400">Not started yet</span>
                    ) : (
                      <span className={`font-semibold ${a.rate >= 90 ? "text-emerald-700" : a.rate >= 70 ? "text-amber-600" : "text-red-600"}`}>{a.rate}% ({a.actual}/{a.expected})</span>
                    )}
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${a.schedule_mismatch ? "bg-amber-500" : a.rate == null ? "bg-slate-200" : a.rate >= 90 ? "bg-emerald-600" : a.rate >= 70 ? "bg-amber-500" : "bg-red-500"}`} style={{ width: `${a.rate == null ? 0 : Math.min(100, a.rate)}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </Section>
        </div>
      )}

      {tab === "users" && (
        <div className="space-y-4">
          <Section title="Create user">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <Input label="Name" testId="user-name-input" value={uForm.name} onChange={(e) => setUForm({ ...uForm, name: e.target.value })} />
              <Input label="Email" testId="user-email-input" value={uForm.email} onChange={(e) => setUForm({ ...uForm, email: e.target.value })} />
              <Input label="Temp password" testId="user-password-input" value={uForm.password} onChange={(e) => setUForm({ ...uForm, password: e.target.value })} />
              <div>
                <label className="text-xs font-bold tracking-[0.06em] uppercase text-slate-500">Role</label>
                <select data-testid="user-role-select" value={uForm.role} onChange={(e) => setUForm({ ...uForm, role: e.target.value })} className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm">
                  {["aide", "sped_teacher", "coordinator", "school_admin", "supervisor", "tag_admin"].map((r) => <option key={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold tracking-[0.06em] uppercase text-slate-500">School</label>
                <select data-testid="user-school-select" value={uForm.school_id} onChange={(e) => setUForm({ ...uForm, school_id: e.target.value })} className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm">
                  <option value="">— none (TAG-wide) —</option>
                  {schools.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div className="flex items-end">
                <button data-testid="create-user-btn" onClick={() => post("/users", { ...uForm, school_id: uForm.school_id || null, school_ids: uForm.role === "supervisor" && uForm.school_id ? [uForm.school_id] : null }, () => setUForm({ name: "", email: "", password: "", role: "aide", school_id: "" }))}
                  className="bg-emerald-700 hover:bg-emerald-800 text-white font-semibold rounded-full px-6 py-2.5 text-sm w-full">Create user</button>
              </div>
            </div>
          </Section>
          <Section title={`All users (${users.length})`}>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs uppercase text-slate-400 border-b border-slate-100"><th className="py-2 pr-4">Name</th><th className="pr-4">Email</th><th className="pr-4">Role</th><th className="pr-4">School</th><th className="pr-4">Last login</th><th>Status</th></tr></thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="border-b border-slate-50">
                      <td className="py-2.5 pr-4 font-medium text-slate-800">{u.name}</td>
                      <td className="pr-4 text-slate-500">{u.email}</td>
                      <td className="pr-4 text-slate-500">{u.role}</td>
                      <td className="pr-4 text-slate-500">{schoolName(u.school_id) !== "—" ? schoolName(u.school_id) : (u.school_ids || []).map(schoolName).join(", ") || "—"}</td>
                      <td className="pr-4 text-slate-400 text-xs">{u.last_login ? u.last_login.slice(0, 16).replace("T", " ") : "Never"}</td>
                      <td>
                        <button data-testid={`toggle-user-status-${u.id}`} onClick={() => setStatus(u.id, u.status === "active" ? "inactive" : "active")}
                          className={`text-xs font-semibold px-3 py-1.5 rounded-full ${u.status === "active" ? "bg-emerald-50 text-emerald-700 hover:bg-red-50 hover:text-red-600" : "bg-red-50 text-red-600 hover:bg-emerald-50 hover:text-emerald-700"}`}>
                          {u.status}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>
        </div>
      )}

      {tab === "students" && (
        <div className="space-y-4">
          <Section title="Add student">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
              <Input label="First name" testId="student-first-input" value={sForm.first_name} onChange={(e) => setSForm({ ...sForm, first_name: e.target.value })} />
              <Input label="Last name" testId="student-last-input" value={sForm.last_name} onChange={(e) => setSForm({ ...sForm, last_name: e.target.value })} />
              <Input label="Grade" testId="student-grade-input" value={sForm.grade} onChange={(e) => setSForm({ ...sForm, grade: e.target.value })} />
              <div>
                <label className="text-xs font-bold tracking-[0.06em] uppercase text-slate-500">School</label>
                <select data-testid="student-school-select" value={sForm.school_id} onChange={(e) => setSForm({ ...sForm, school_id: e.target.value })} className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm">
                  <option value="">Select…</option>
                  {schools.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div className="flex items-end">
                <button data-testid="create-student-btn" onClick={() => post("/students", sForm, () => setSForm({ first_name: "", last_name: "", grade: "", school_id: "" }))}
                  disabled={!sForm.school_id || !sForm.first_name}
                  className="bg-emerald-700 hover:bg-emerald-800 text-white font-semibold rounded-full px-6 py-2.5 text-sm w-full disabled:opacity-50">Add</button>
              </div>
            </div>
          </Section>
          <Section title={`Students (${students.length})`}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {students.map((s) => (
                <a key={s.id} href={`/students/${s.id}`} data-testid={`admin-student-${s.id}`} className="border border-slate-100 rounded-lg p-4 hover:shadow-md transition-shadow duration-200 block">
                  <p className="font-medium text-slate-800">{s.first_name} {s.last_name}</p>
                  <p className="text-xs text-slate-400">Grade {s.grade} · {schoolName(s.school_id)} · Aides: {(s.aides || []).map((a) => a.name).join(", ") || "None"}</p>
                </a>
              ))}
            </div>
          </Section>
        </div>
      )}

      {tab === "assignments" && (
        <div className="space-y-4">
          <Section title="Create assignment">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div>
                <label className="text-xs font-bold tracking-[0.06em] uppercase text-slate-500">Aide</label>
                <select data-testid="assign-aide-select" value={aForm.aide_id} onChange={(e) => setAForm({ ...aForm, aide_id: e.target.value })} className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm">
                  <option value="">Select…</option>
                  {users.filter((u) => u.role === "aide").map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold tracking-[0.06em] uppercase text-slate-500">Student</label>
                <select data-testid="assign-student-select" value={aForm.student_id} onChange={(e) => {
                  const st = students.find((s) => s.id === e.target.value);
                  setAForm({ ...aForm, student_id: e.target.value, school_id: st?.school_id || "" });
                }} className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm">
                  <option value="">Select…</option>
                  {students.map((s) => <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>)}
                </select>
              </div>
              <Input label="Start date" testId="assign-start-input" type="date" value={aForm.start_date} onChange={(e) => setAForm({ ...aForm, start_date: e.target.value })} />
              <div className="flex items-end">
                <button data-testid="create-assignment-btn" onClick={() => post("/assignments", aForm, () => setAForm({ ...aForm, aide_id: "", student_id: "" }))}
                  disabled={!aForm.aide_id || !aForm.student_id}
                  className="bg-emerald-700 hover:bg-emerald-800 text-white font-semibold rounded-full px-6 py-2.5 text-sm w-full disabled:opacity-50">Assign</button>
              </div>
            </div>
          </Section>
          <Section title="Active assignments">
            <div className="space-y-2">
              {assignments.map((a) => {
                const aide = users.find((u) => u.id === a.aide_id);
                const st = students.find((s) => s.id === a.student_id);
                return (
                  <div key={a.id} className="flex items-center justify-between gap-3 border border-slate-100 rounded-lg p-3.5 flex-wrap">
                    <p className="text-sm text-slate-700"><span className="font-medium">{aide?.name || "?"}</span> → {st ? `${st.first_name} ${st.last_name}` : a.classroom_id ? "Classroom" : "?"} <span className="text-xs text-slate-400">since {a.start_date} · {a.status}</span></p>
                    {a.status === "active" && (
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-bold uppercase tracking-wide text-slate-500">Doc start</span>
                        <input data-testid={`doc-start-input-${a.id}`} type="date" defaultValue={a.doc_start_date || a.start_date}
                          onBlur={async (e) => {
                            const v = e.target.value;
                            if (!v || v === (a.doc_start_date || a.start_date)) return;
                            try { await api.put(`/assignments/${a.id}/doc-start`, { doc_start_date: v }); toast.success("Documentation start date updated"); load(); }
                            catch (err) { toast.error(apiError(err)); }
                          }}
                          className="rounded-md border border-slate-200 px-2 py-1 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-emerald-600" />
                        <button data-testid={`end-assignment-btn-${a.id}`} onClick={async () => { await api.put(`/assignments/${a.id}/end`); load(); }}
                          className="text-xs font-semibold text-red-600 bg-red-50 hover:bg-red-100 px-3 py-1.5 rounded-full">End</button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </Section>
        </div>
      )}

      {tab === "schools" && (
        <div className="space-y-4">
          <Section title="Add client school (tenant)">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <Input label="School name" testId="school-name-input" value={scForm.name} onChange={(e) => setScForm({ ...scForm, name: e.target.value })} />
              <Input label="Contract start" testId="school-start-input" type="date" value={scForm.contract_start} onChange={(e) => setScForm({ ...scForm, contract_start: e.target.value })} />
              <Input label="Contract end" testId="school-end-input" type="date" value={scForm.contract_end} onChange={(e) => setScForm({ ...scForm, contract_end: e.target.value })} />
              <div className="flex items-end">
                <button data-testid="create-school-btn" onClick={() => post("/schools", scForm, () => setScForm({ name: "", contract_start: "", contract_end: "" }))}
                  disabled={!scForm.name}
                  className="bg-emerald-700 hover:bg-emerald-800 text-white font-semibold rounded-full px-6 py-2.5 text-sm w-full disabled:opacity-50">Create portal</button>
              </div>
            </div>
          </Section>
          <Section title="Client portals">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {schools.map((s) => (
                <div key={s.id} className="border border-slate-100 rounded-lg p-4">
                  <p className="font-medium text-slate-800">{s.name}</p>
                  <p className="text-xs text-slate-400 mt-0.5">Contract: {s.contract_start || "—"} → {s.contract_end || "—"} · {s.status}</p>
                  <p className="text-xs text-slate-400">Isolated tenant · {s.contacts?.[0]?.name ? `${s.contacts[0].name}${s.contacts[0].title ? ` (${s.contacts[0].title})` : ""}` : "No contact"}</p>
                  <ContactEditor school={s} onSaved={load} />
                  <div className="mt-3 flex items-center gap-2">
                    <span className="text-[10px] font-bold uppercase tracking-wide text-slate-500">End-of-day reminder</span>
                    <input data-testid={`reminder-time-input-${s.id}`} type="time"
                      value={remTimes[s.id] ?? s.settings?.eod_reminder_time ?? "15:30"}
                      onChange={(e) => setRemTimes((r) => ({ ...r, [s.id]: e.target.value }))}
                      className="rounded-md border border-slate-200 px-2 py-1 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-emerald-600" />
                    <button data-testid={`save-reminder-time-btn-${s.id}`} onClick={() => saveReminderTime(s)}
                      className="text-xs font-semibold bg-emerald-50 text-emerald-700 hover:bg-emerald-100 px-3 py-1.5 rounded-full transition-colors duration-150">Save</button>
                  </div>
                  <BrandingEditor school={s} onSaved={load} />
                </div>
              ))}
            </div>
          </Section>
        </div>
      )}

      {tab === "audit" && (
        <Section title="Audit log (every read and write is recorded)">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs uppercase text-slate-400 border-b border-slate-100"><th className="py-2 pr-4">Time (UTC)</th><th className="pr-4">User</th><th className="pr-4">Role</th><th className="pr-4">Action</th><th>Entity</th></tr></thead>
              <tbody>
                {audit.map((a) => (
                  <tr key={a.id} className="border-b border-slate-50">
                    <td className="py-2 pr-4 text-xs font-mono text-slate-400">{a.timestamp.slice(0, 19).replace("T", " ")}</td>
                    <td className="pr-4 text-slate-700">{a.user_name}</td>
                    <td className="pr-4 text-slate-500 text-xs">{a.role}</td>
                    <td className="pr-4"><span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${a.action.includes("read") ? "bg-slate-100 text-slate-500" : "bg-emerald-50 text-emerald-700"}`}>{a.action}</span></td>
                    <td className="text-slate-500 text-xs">{a.entity_type}
                      {a.original?.previewing_as && <span className="ml-1.5 text-[10px] font-semibold text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded-full">preview: {a.original.previewing_as} @ {a.original.school}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}
    </div>
  );
}
